import {
  Align,
  Arrange,
  Button,
  Container,
  Icon,
  Loader,
  Spacer,
  SvgClock,
  SvgInfo,
  Text,
  TextButton,
} from '@loomhq/lens';
import pick from 'lodash/pick';
import React, { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import { updateShareVideoInTrimMode } from 'actions/shareVideo';
import {
  setSelectedPlayRangeIdx,
  updateShowTrimExitWarning,
} from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  updateVideoPlayerEnded,
  updateVideoPlayerNotEnded,
  updateVideoPlayerSourceFromClip,
} from 'actions/videoPlayer';
import {
  CLIP_VIDEO_SOURCE_URL_UPDATED,
  SAVE_WEAVED_VIDEO,
  TRIM_CANCELLED,
  TRIM_CHANGE_MADE,
  TRIM_DELETE,
  TRIM_HELP_ARTICLE_CLICKED,
  TRIM_REVERT_TO_ORIGINAL,
} from 'constants/events';
import { V4, V6 } from 'constants/recordingVersions';
import { MIN_PLAY_RANGE_DURATION } from 'constants/trimming';
import { leaveEditModeCreator, leaveTrimModeCreator } from 'creators/trimming';
import { selectSelectedPlayRangeIdx } from 'selectors/trimming';
import { selectVideoId } from 'selectors/video';
import {
  selectPlayerTime,
  selectRequestedPlayerTime,
  selectVideo,
  selectVideoDuration,
  selectVideoPlayerIsFetching,
  selectVideoPlayerReadyToPlayOnSharePage,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import { secondsToVideoTS } from 'utilities/time';
import { findCurrentPlayRangeIdx } from 'utilities/trimming';

import ClipEditor from './clip-editor';
import TestingClipsControls from './clips';
import TrimControlBar from './control-bar';
import { useClipsState } from './clips/state';
import { DeleteDraftClips } from './graphql/DeleteDraftClips.gql';
import { PrepareForEdit } from './graphql/PrepareForEdit.gql';
import { UpdateVideoClips } from './graphql/UpdateVideoClips.gql';
import { Video } from '../../../../../shared/types/clips';

import styles from './styles.module.less';

const updateVideoClips = async (videoId, clips) => {
  const gqlClient = getGraphQLClient();

  const clipsData = clips.map(clip => ({
    id: clip.id,
    position: clip.position,
    trim_ranges: clip.trimRanges.map(range => pick(range, ['from', 'to'])),
  }));

  const { data } = await gqlClient.mutate({
    mutation: UpdateVideoClips,
    variables: {
      videoId,
      clips: clipsData,
    },
    fetchPolicy: 'no-cache',
  });

  return data;
};

// TODO fix for weave
const findClosestPlayRangeAndLoc = (time, playRanges) => {
  let playRangeIdx: number;
  let closestLoc: number;
  let distance = Infinity;

  for (let idx = 0; idx < playRanges.length; idx++) {
    const range = playRanges[idx];

    if (Math.abs(range.from - time) < distance) {
      closestLoc = range.from;
      playRangeIdx = idx;
      distance = Math.abs(range.from - time);
    }

    if (Math.abs(range.to - time) < distance) {
      closestLoc = range.to;
      playRangeIdx = idx;
      distance = Math.abs(range.to - time);
    }
  }

  // @ts-expect-error ignore due to enabling strict null checks
  return { closestLoc, playRangeIdx };
};

const EditTools = (): JSX.Element => {
  const {
    clips,
    addClip,
    undo,
    redo,
    removeClip,
    focusedPlayRangeIndex,
    removePlayRangeFromClip,
    hasBeenUpdated,
    resetState,
    focusedClipIndex,
    updateFocusedClipIndex,
    updateCurrentClipDuration,
    updateFocusedPlayRangeIndex,
    canUndo,
    canRedo,
    canDeletePlayRange,
    hasTrims,
    currentClip,
    // currentPlayRange,
    playRanges,
    trimVideoDuration,
    duration,
    splitCurrentPlayRange,
  } = useClipsState(state => ({
    addClip: state.addClip,
    clips: state.clips,
    focusedClipIndex: state.focusedClipIndex,
    focusedPlayRangeIndex: state.focusedPlayRangeIndex,
    hasBeenUpdated: state.hasBeenUpdated,
    removeClip: state.removeClip,
    removePlayRangeFromClip: state.removePlayRangeFromClip,
    resetState: state.resetState,
    updateFocusedClipIndex: state.updateFocusedClipIndex,
    undo: state.undo,
    redo: state.redo,
    updateCurrentClipDuration: state.updateCurrentClipDuration,
    updateFocusedPlayRangeIndex: state.updateFocusedPlayRangeIndex,
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    canDeletePlayRange: state.canDeletePlayRange,
    getDuration: state.getDuration,
    hasTrims: state.hasTrims,
    trimVideoDuration: state.getTrimVideoDuration(),
    currentClip: state.getCurrentClip(),
    currentPlayRange: state.getCurrentPlayRanges()[state.focusedPlayRangeIndex],
    playRanges: state.getCurrentPlayRanges(),
    duration: state.getDuration(),
    splitCurrentPlayRange: state.splitCurrentPlayRange,
  }));

  const dispatch = useDispatch();
  const currentTime: number = useSelector(selectPlayerTime);
  const requestedCurrentTime = useSelector(selectRequestedPlayerTime);
  const videoDuration: number = useSelector(selectVideoDuration);
  const video: Video = useSelector(selectVideo);
  const videoId: string = useSelector(selectVideoId);

  // TODO fix/eliminate all following for weave
  const selectedPlayRangeIdx: number = useSelector(selectSelectedPlayRangeIdx);
  const isFetching = useSelector(selectVideoPlayerIsFetching);
  const readyToPlay = useSelector(selectVideoPlayerReadyToPlayOnSharePage);

  const selectedPlayRange =
    selectedPlayRangeIdx != null ? playRanges[selectedPlayRangeIdx] : null;
  const currentTimeInSelectedPlayRangeBounds = Boolean(
    selectedPlayRange &&
      currentTime - selectedPlayRange.from >= MIN_PLAY_RANGE_DURATION &&
      selectedPlayRange.to - currentTime >= MIN_PLAY_RANGE_DURATION
  );
  const canSplitPlayRange = Boolean(
    playRanges.length > 0 &&
      requestedCurrentTime == null &&
      currentTimeInSelectedPlayRangeBounds
  );

  // This effect is here b/c we do not have an accurate duration until the
  // video loads. The duration in video_player is the duration of the
  // trimmed video and the duration in video_player.video.video_properties
  // is rounded which can mess up our play range values. We will only update
  // the current clip duration if there is a change in the duration stored in
  // redux compared to the clips current duration is within 1 second.
  useEffect(() => {
    const currentDuration = currentClip?.duration || 0;
    const videoCurrentDurationDifference = Math.abs(
      videoDuration - currentDuration
    );

    if (
      clips.length > 0 &&
      focusedClipIndex != null &&
      Number(videoDuration) > 0 &&
      (currentDuration === 0 ||
        (videoCurrentDurationDifference > 0 &&
          videoCurrentDurationDifference < 1))
    ) {
      updateCurrentClipDuration(videoDuration);
    }
  }, [
    dispatch,
    videoDuration,
    clips.length,
    focusedClipIndex,
    updateCurrentClipDuration,
    currentClip,
  ]);

  const trimButtonsRef = useCallback(buttonsNode => {
    if (buttonsNode) {
      buttonsNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, []);

  useHotkeys(
    'cmd+z,ctrl+z',
    e => {
      e.preventDefault();
      undo(videoId);
    },
    { enabled: canUndo() }
  );

  useHotkeys(
    'cmd+shift+z,ctrl+shift+z',
    e => {
      e.preventDefault();
      redo(videoId);
    },
    {
      enabled: canRedo(),
    }
  );

  // Handle URL changes, back button, page reload with unsaved changes
  const unloadEventListenerRef = useRef(() => '');

  useEffect(() => {
    const removeEventListener = () => {
      if (unloadEventListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          unloadEventListenerRef.current,
          { capture: true }
        );
      }
    };

    const showWarningModal = event => {
      event.preventDefault();

      return (event.returnValue =
        'There are unsaved changes to your video. Are you sure you want to discard them?');
    };

    removeEventListener();

    if (hasBeenUpdated) {
      // @ts-expect-error ignore due to enabling strict null checks
      unloadEventListenerRef.current = showWarningModal;
      window.addEventListener('beforeunload', unloadEventListenerRef.current, {
        capture: true,
      });
    }

    return removeEventListener;
  }, [hasBeenUpdated, dispatch]);

  const prepareForEditing = useCallback(
    async (videoId, addClip) => {
      const gqlClient = getGraphQLClient();

      const { data } = await gqlClient.mutate({
        mutation: PrepareForEdit,
        variables: {
          videoId,
        },
        fetchPolicy: 'no-cache',
      });

      if (data.message == null) {
        for (const clip of data.prepareForEdit.video.clips) {
          await addClip(clip, true);

          if (clip?.position === 1 && clip?.source_url) {
            dispatch(
              updateVideoPlayerSourceFromClip({
                sourceUrl: clip?.source_url,
                sourceMimeType: clip?.source_mime_type,
                partCredentials: clip?.source_part_credentials,
                sourceUrlIsTranscoded: clip?.source_url_is_transcoded,
              })
            );

            analytics.track(CLIP_VIDEO_SOURCE_URL_UPDATED, {
              id: videoId,
              clipId: clip?.id,
              event_type: 'on video load',
            });
          }
        }
      } else {
        // TODO: handle error when no clips
      }
    },
    [dispatch]
  );

  useEffect(() => {
    // V4 video (uploads) are not supported in weave at this time
    if (video.video_properties.recording_version !== V4) {
      prepareForEditing(videoId, addClip);
    }
  }, [
    videoId,
    addClip,
    video.video_properties.recording_version,
    prepareForEditing,
  ]);

  // TODO redo for Weave
  const updateSelectedPlayRange = () => {
    if (requestedCurrentTime != null) {
      return;
    }

    if (
      !playRanges[selectedPlayRangeIdx] ||
      currentTime < playRanges[selectedPlayRangeIdx].from ||
      currentTime > playRanges[selectedPlayRangeIdx].to
    ) {
      const idx = findCurrentPlayRangeIdx(currentTime, playRanges);

      if (idx !== -1) {
        dispatch(setSelectedPlayRangeIdx(idx));
      }
    }
  };

  /* keep the playhead inside the play ranges */
  // TODO redo for Weave
  const placePlayheadInPlayRanges = () => {
    // If we are in a play range already or time is being updated
    if (selectedPlayRangeIdx !== -1 || requestedCurrentTime != null) {
      return;
    }

    // If we are not in a play range, move to the closest one
    const { closestLoc, playRangeIdx } = findClosestPlayRangeAndLoc(
      currentTime,
      playRanges
    );

    dispatch(setSelectedPlayRangeIdx(playRangeIdx));
    dispatch(requestVideoPlayerCurrentTime(closestLoc));
  };

  useEffect(placePlayheadInPlayRanges, [
    dispatch,
    playRanges,
    currentTime,
    selectedPlayRangeIdx,
    requestedCurrentTime,
  ]);

  useEffect(updateSelectedPlayRange, [
    currentTime,
    playRanges,
    selectedPlayRangeIdx,
    requestedCurrentTime,
    dispatch,
  ]);

  // commented out this piece of code as this does not let a user
  // click into a specific timestamp within a play range.
  // update playhead when focused clip or play range changes
  // useEffect(() => {
  //   if (currentPlayRange) {
  //     dispatch(requestVideoPlayerCurrentTime(currentPlayRange.from));
  //   }
  // }, [dispatch, clips?.length, currentClip?.id, currentPlayRange?.from]);

  // TODO redo for Weave
  useEffect(() => {
    // If we are at the end of the last play range
    if (!currentClip || !currentClip.playRanges) {
      return;
    }

    const lastPlayRangeTo =
      currentClip.playRanges[currentClip.playRanges.length - 1]?.to;

    // if we trim the end of a clip then when the last
    // play range of a clip finishes then the playhead's current
    // time goes to the end time stamp of the video which is
    // greater than the last playranges' to
    if (currentTime >= lastPlayRangeTo) {
      // only dispatch at last clip playrange
      if (focusedClipIndex === clips.length - 1) {
        dispatch(updateVideoPlayerEnded());
      } else {
        dispatch(updateVideoPlayerNotEnded());

        const nextClipIndex = focusedClipIndex + 1;
        const nextClip = clips[nextClipIndex];

        updateFocusedClipIndex(nextClipIndex);

        dispatch(
          updateVideoPlayerSourceFromClip({
            sourceUrl: nextClip?.source_url,
            sourceMimeType: nextClip?.source_mime_type,
            partCredentials: nextClip?.source_part_credentials,
            sourceUrlIsTranscoded: nextClip?.source_url_is_transcoded,
            autoplay: true,
          })
        );

        analytics.track(CLIP_VIDEO_SOURCE_URL_UPDATED, {
          id: videoId,
          clipId: nextClip?.id,
          event_type: 'autoplay',
        });
      }
    } else {
      dispatch(updateVideoPlayerNotEnded());
    }
  }, [
    currentTime,
    playRanges,
    clips,
    currentClip,
    focusedClipIndex,
    updateFocusedClipIndex,
    dispatch,
    videoId,
  ]);

  const deletePlayRange = () => {
    analytics.track(TRIM_DELETE, {
      id: videoId,
      has_been_trimmed: true,
      event_type: 'click',
    });

    if (!playRanges) {
      return;
    }

    if (playRanges.length <= 1) {
      // last play range in the clip, we should just remove the clip itself
      removeClip(focusedClipIndex);
    } else {
      removePlayRangeFromClip(focusedClipIndex, focusedPlayRangeIndex);
    }
  };

  const splitPlayRange = () => {
    splitCurrentPlayRange(currentTime);

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'split',
      hotkey: false,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const saveChanges = async () => {
    analytics.track(SAVE_WEAVED_VIDEO, {
      videoId: video.id,
      clipsCount: clips.length,
      duration,
    });

    const patchedVideo = await updateVideoClips(videoId, clips);

    dispatch(leaveEditModeCreator(patchedVideo));
    dispatch(updateShareVideoInTrimMode(false));
    dispatch(showConfirmationModal('Your changes have been saved.'));
    resetState();
  };

  const exitWithoutSaving = () => {
    if (hasBeenUpdated) {
      dispatch(updateShowTrimExitWarning(true));
    } else {
      goBackToVideo();
    }
  };

  const goBackToVideo = async () => {
    dispatch(leaveTrimModeCreator());

    const gqlClient = getGraphQLClient();

    await gqlClient.mutate({
      mutation: DeleteDraftClips,
      variables: {
        rootVideoId: videoId,
      },
      fetchPolicy: 'no-cache',
    });

    analytics.track(TRIM_CANCELLED, {
      id: videoId,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const revertToOriginal = () => {
    // TODO

    analytics.track(TRIM_REVERT_TO_ORIGINAL, {
      id: videoId,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const canRevertToOriginal =
    playRanges != null &&
    playRanges.length > 0 &&
    (playRanges[0]?.from != 0 || playRanges[0]?.to != duration);

  const trimControlBarProps = {
    currentTime,
    canSplitPlayRange,
    splitPlayRange,
    canDeletePlayRange: canDeletePlayRange(),
    deletePlayRange,
    canUndo: canUndo(),
    canRedo: canRedo(),
    undo: () => undo(videoId),
    redo: () => redo(videoId),
    revertToOriginal,
    canRevertToOriginal,
    videoId,
  };

  const isReady =
    !isFetching &&
    /* readyToPlay OR if you are V6 we are going to temporarily
    load the page anyway instead of showing a loading spinner */
    (readyToPlay || video?.video_properties?.recording_version === V6);

  if (!isReady) {
    return (
      <Container paddingTop="xlarge">
        <Align alignment="center">
          <Loader size="large" />
        </Align>
      </Container>
    );
  }

  const durationChanged = duration != trimVideoDuration;

  const allClipsDuration = clips.reduce(
    (acc, c) => acc + c.video_properties.duration,
    0
  );

  const handleClick = (
    event: React.MouseEvent,
    { secs, clipIdx }: { secs: number; clipIdx: number }
  ) => {
    if (clipIdx !== -1 && clipIdx < clips.length) {
      const clip = clips[clipIdx];

      for (const [rangeIdx, { from, to }] of clip.playRanges?.entries() || []) {
        if (secs >= from && secs <= to) {
          console.log({ secs });
          updateFocusedPlayRangeIndex(rangeIdx);
          dispatch(requestVideoPlayerCurrentTime(secs));
        }
      }
    }
  };

  return (
    <Arrange justifyContent="stretch" autoFlow="row">
      <Spacer top="large" />
      <TrimControlBar {...trimControlBarProps} />
      <Spacer top="small" />
      <Arrange
        gap="large"
        columns={clips.map(
          clip => `${clip.video_properties.duration / allClipsDuration}fr`
        )}
      >
        {clips.map((clip, idx) => (
          <ClipEditor
            key={clip.id}
            clip={clip}
            idx={idx}
            onClick={handleClick}
            currentTime={currentTime}
          />
        ))}
      </Arrange>
      <div ref={trimButtonsRef}>
        <Align alignment="center">
          <div className={styles.durationChange}>
            {durationChanged ? (
              <Arrange gap="xsmall">
                <Icon color="bodyDimmed" icon={<SvgClock />} />{' '}
                <Text
                  size="medium"
                  color="bodyDimmed"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(duration, 1)}
                </Text>{' '}
                <Text size="medium" color="bodyDimmed" fontWeight="medium">
                  &rarr;
                </Text>
                <Text
                  size="medium"
                  color="body"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(trimVideoDuration, 1)}
                </Text>
              </Arrange>
            ) : (
              <Text size="medium">
                Trim your video by dragging the handles and using the Split
                button.
              </Text>
            )}
          </div>

          <TextButton
            size="small"
            icon={<Icon size={2} icon={<SvgInfo />} color="primary" />}
            htmlTag="a"
            // @ts-expect-error removed lens.d.ts
            target="_blank"
            href="https://support.loom.com/hc/en-us/articles/360002217478-How-to-trim-your-Loom-video-"
            onClick={() =>
              analytics.track(TRIM_HELP_ARTICLE_CLICKED, {
                id: videoId,
                has_been_trimmed: hasTrims,
                event_type: 'click',
              })
            }
          >
            <Text color="primary" size="small" fontWeight="medium">
              View help article
            </Text>
          </TextButton>

          <Spacer top="medium" />
          <Arrange gap="small">
            <Button onClick={exitWithoutSaving}>Cancel</Button>
            <Button
              variant="primary"
              isDisabled={!hasBeenUpdated}
              onClick={saveChanges}
            >
              Save changes
            </Button>
          </Arrange>
          <TestingClipsControls />
        </Align>
      </div>
    </Arrange>
  );
};

export default EditTools;
